/**
 * Business types and the feature modules each one can use.
 * This file is the single source of truth for module gating: the API enforces it
 * (requireModule) and the web app uses it to decide which screens exist.
 */

export const BUSINESS_TYPES = ['supermarket', 'butchery', 'furniture'] as const;
export type BusinessType = (typeof BUSINESS_TYPES)[number];

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  supermarket: 'Supermarket',
  butchery: 'Butchery',
  furniture: 'Furniture shop',
};

/** How a module relates to a business type. */
export type ModuleAvailability =
  | 'always' // part of this business type, cannot be switched off
  | 'default_on' // on by default, owner may switch off
  | 'optional' // off by default, owner may switch on
  | 'unavailable'; // not offered for this business type

export const MODULE_KEYS = [
  'barcode',
  'expiry',
  'promotions',
  'weighing',
  'yields',
  'wastage',
  'variants',
  'orders',
  'deliveries',
] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

export interface ModuleDefinition {
  key: ModuleKey;
  label: string;
  description: string;
  /** Phase of the roadmap in which the module's screens are delivered. */
  phase: number;
  availability: Record<BusinessType, ModuleAvailability>;
}

export const MODULES: Record<ModuleKey, ModuleDefinition> = {
  barcode: {
    key: 'barcode',
    label: 'Barcode scanning',
    description: 'Scan product barcodes at the till and when receiving stock.',
    phase: 2,
    availability: { supermarket: 'always', butchery: 'optional', furniture: 'optional' },
  },
  expiry: {
    key: 'expiry',
    label: 'Expiry & batch tracking',
    description: 'Record batch numbers and expiry dates, sell first-expiring stock first, get near-expiry alerts.',
    phase: 4,
    availability: { supermarket: 'always', butchery: 'optional', furniture: 'unavailable' },
  },
  promotions: {
    key: 'promotions',
    label: 'Promotions',
    description: 'Time-limited price cuts, percentage discounts and buy-X-get-Y offers applied automatically at the till.',
    phase: 4,
    availability: { supermarket: 'default_on', butchery: 'optional', furniture: 'optional' },
  },
  weighing: {
    key: 'weighing',
    label: 'Weight-based selling',
    description: 'Sell by the kilogram, read scale-printed barcodes with embedded weight or price.',
    phase: 4,
    availability: { supermarket: 'optional', butchery: 'always', furniture: 'unavailable' },
  },
  yields: {
    key: 'yields',
    label: 'Carcass breakdown & yields',
    description: 'Break carcasses into cuts, measure yield percentages and allocate cost to each cut.',
    phase: 4,
    availability: { supermarket: 'unavailable', butchery: 'always', furniture: 'unavailable' },
  },
  wastage: {
    key: 'wastage',
    label: 'Wastage tracking',
    description: 'Record trim, spoilage, expired and damaged stock with reasons and cost.',
    phase: 4,
    availability: { supermarket: 'optional', butchery: 'always', furniture: 'optional' },
  },
  variants: {
    key: 'variants',
    label: 'Product variants',
    description: 'One product in several colours, fabrics, finishes or sizes, each with its own stock and price.',
    phase: 4,
    availability: { supermarket: 'unavailable', butchery: 'unavailable', furniture: 'always' },
  },
  orders: {
    key: 'orders',
    label: 'Customer orders & deposits',
    description: 'Take orders and deposits (lay-by), track balances and collect the rest on delivery or collection.',
    phase: 4,
    availability: { supermarket: 'unavailable', butchery: 'optional', furniture: 'always' },
  },
  deliveries: {
    key: 'deliveries',
    label: 'Deliveries',
    description: 'Schedule deliveries, assign drivers and record proof of delivery.',
    phase: 4,
    availability: { supermarket: 'unavailable', butchery: 'unavailable', furniture: 'default_on' },
  },
};

/** Which modules are switched on for a business, given its type and the owner's optional choices. */
export type ModuleOverrides = Partial<Record<ModuleKey, boolean>>;

export function resolveEnabledModules(type: BusinessType, overrides: ModuleOverrides = {}): ModuleKey[] {
  const out: ModuleKey[] = [];
  for (const key of MODULE_KEYS) {
    const a = MODULES[key].availability[type];
    if (a === 'always') out.push(key);
    else if (a === 'default_on' && overrides[key] !== false) out.push(key);
    else if (a === 'optional' && overrides[key] === true) out.push(key);
  }
  return out;
}

/**
 * Removes overrides that don't apply to the business type (e.g. after the type changes),
 * so stored settings never contain meaningless switches.
 */
export function sanitizeModuleOverrides(type: BusinessType, overrides: ModuleOverrides): ModuleOverrides {
  const clean: ModuleOverrides = {};
  for (const key of toggleableModules(type)) {
    if (typeof overrides[key] === 'boolean') clean[key] = overrides[key];
  }
  return clean;
}

/** Modules an owner may toggle for this business type. */
export function toggleableModules(type: BusinessType): ModuleKey[] {
  return MODULE_KEYS.filter((k) => {
    const a = MODULES[k].availability[type];
    return a === 'optional' || a === 'default_on';
  });
}
